import React, { useEffect, useId, useRef, useState } from "react";
import { FaCalendarAlt } from "react-icons/fa";
import {
  isoToMMDDYYYY,
  normalizeComplianceDeadline,
} from "../utils/listingComplianceDeadline";

/**
 * Compliance deadline input shown as MM/DD/YYYY, with a calendar picker.
 * Stores/returns YYYY-MM-DD to the parent via onChange.
 */
const ComplianceDeadlineInput = ({
  id,
  value,
  onChange,
  disabled,
  className = "",
}) => {
  const autoId = useId();
  const inputId = id || autoId;
  const datePickerRef = useRef(null);
  const [display, setDisplay] = useState(() => isoToMMDDYYYY(value));

  useEffect(() => {
    setDisplay(isoToMMDDYYYY(value));
  }, [value]);

  const commit = (raw) => {
    const trimmed = String(raw || "").trim();
    if (!trimmed) {
      setDisplay("");
      onChange?.("");
      return;
    }
    const iso = normalizeComplianceDeadline(trimmed);
    if (!iso) {
      setDisplay(isoToMMDDYYYY(value));
      return;
    }
    setDisplay(isoToMMDDYYYY(iso));
    onChange?.(iso);
  };

  const openCalendar = () => {
    if (disabled) return;
    const el = datePickerRef.current;
    if (!el) return;
    if (typeof el.showPicker === "function") {
      try {
        el.showPicker();
        return;
      } catch {
        // fall through to focus/click
      }
    }
    el.focus();
    el.click();
  };

  return (
    <div className="relative w-full sm:w-56">
      <input
        id={inputId}
        type="text"
        inputMode="numeric"
        placeholder="mm/dd/yyyy"
        value={display}
        disabled={disabled}
        onChange={(e) => setDisplay(e.target.value)}
        onBlur={() => commit(display)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            commit(display);
          }
        }}
        className={
          className ||
          "block w-full rounded-md border-gray-300 shadow-sm text-sm pr-10 focus:border-blue-700 focus:ring-blue-700 disabled:bg-gray-100"
        }
      />
      <button
        type="button"
        onClick={openCalendar}
        disabled={disabled}
        aria-label="Open calendar"
        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-blue-900 disabled:opacity-40"
      >
        <FaCalendarAlt className="h-4 w-4" />
      </button>
      <input
        ref={datePickerRef}
        type="date"
        value={normalizeComplianceDeadline(value) || ""}
        disabled={disabled}
        onChange={(e) => {
          const iso = normalizeComplianceDeadline(e.target.value);
          setDisplay(isoToMMDDYYYY(iso));
          onChange?.(iso);
        }}
        tabIndex={-1}
        aria-hidden="true"
        className="absolute right-0 top-0 h-full w-10 opacity-0 cursor-pointer disabled:cursor-not-allowed"
      />
    </div>
  );
};

export default ComplianceDeadlineInput;
